import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Question, Warning } from "@phosphor-icons/react"
import { Tag } from "@/components/ui/tag"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { formatDateString } from "@/lib/format-date"
import { QuestionnaireDrawer } from "@/modules/patients/detail/progress-note/components/questionnaire-drawer"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { QuestionnaireCriticalitySummaryFragment$key } from "@/modules/patients/services/__generated__/QuestionnaireCriticalitySummaryFragment.graphql"
import { QuestionnaireCriticalitySummaryFragment } from "@/modules/patients/services/QuestionnaireCriticalitySummaryFragment"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { AsyncRequestStatus, CommunicationChannel } from "@/types/__generated__/union-types"

interface QuestionnaireProps {
  completedAt: string
  name: string
  questionnaireCriticalitySummary: EpisodeOfCareProgressNoteData["appointment"]["questionnaireCriticalitySummary"]
  appointmentId: string
  questionnaireId: number | null
  asyncRequestStatus: AsyncRequestStatus | null
  channel: CommunicationChannel
}

const QuestionnaireDrawerName = "QuestionnaireDrawer"

const QuestionnaireReadOnly = (props: QuestionnaireProps) => {
  const {
    completedAt,
    name,
    questionnaireCriticalitySummary,
    appointmentId,
    questionnaireId,
    asyncRequestStatus,
    channel,
  } = props

  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)

  const { t } = useTranslation()

  const criticalitySummary = useFragment<QuestionnaireCriticalitySummaryFragment$key>(
    QuestionnaireCriticalitySummaryFragment,
    questionnaireCriticalitySummary,
  )

  if (!criticalitySummary) {
    return null
  }

  const { criticals, warnings, infos, questionnaireType } = criticalitySummary

  const handleOpenQuestionnaireDrawer = () => {
    if (drawerName === QuestionnaireDrawerName) return
    openDrawer(
      () => (
        <QuestionnaireDrawer
          questionnaireId={questionnaireId}
          appointmentId={appointmentId}
          completedAt={completedAt}
          channel={channel}
          isReadOnly={true}
          name={name}
          type={questionnaireType}
        />
      ),
      QuestionnaireDrawerName,
    )
  }

  return (
    <button
      className="hover:bg-surface-hovered flex cursor-pointer flex-row items-center justify-between gap-2 rounded-lg px-2 py-1"
      onClick={() => {
        FaroInsightsWrapper.pushEvent("questionnaires_open_readonly")
        handleOpenQuestionnaireDrawer()
      }}>
      <div className="flex items-center gap-2 text-left">
        <Question className="text-on-surface-subdued h-6 w-6 shrink-0" />
        <span className="flex flex-col">
          <p>
            {t("patient.questionnaire.title")}: {name}
          </p>
          <p className="text-on-surface-subdued text-xs">
            {t("patient.questionnaire.completedAt")} {formatDateString(completedAt)}
          </p>
        </span>
      </div>
      {asyncRequestStatus === "APPROVED" && (
        <Tag hasIcon={false} type="success" className="ml-auto" label={t("common.button.approved")} />
      )}
      {asyncRequestStatus === "REJECTED" && (
        <Tag hasIcon={false} type="critical" className="ml-auto" label={t("common.button.rejected")} />
      )}
      <div className="flex items-end gap-2">
        {criticals > 0 && <Tag hasIcon={true} type="critical" className="ml-auto" label={criticals.toString()} />}
        {warnings > 0 && <Tag hasIcon={true} type="warning" className="ml-auto" label={warnings.toString()} />}
        {infos > 0 && (
          <Tag
            hasIcon={true}
            icon={<Warning size={16} />}
            type="neutral"
            className="ml-auto"
            label={infos.toString()}
          />
        )}
      </div>
    </button>
  )
}

export { QuestionnaireReadOnly }
